import React, { Fragment, useState, useEffect } from "react";
import { Button } from "reactstrap";
import moment from "moment";
import { FormattedNumber } from "../../FormattedNumber/FormattedNumber";
import { useSelector } from "react-redux";
import { useDebounce } from "bx-react-ui/src/utils/hooks";
import { CircularProgress } from "../../UI/CircularProgress/CircularProgress";
import { Icons } from "../../UI/Icon/Icon";
import { Row, Col } from "../../UI/Customstrap/Customstrap";
import GenericButton from "../../../components/UI/Button/Button";
import confirmIcon from "../../../../public/svg/ButtonIcons/right-arrow.svg";
import { getEarnings, getPrechecks, takeOffer } from "../../../services/api/inout";
import { getSavedPersonInfo, getCurrentAccount, getTexts } from "../../../reducers/selectors";
import { showToast } from "../../../utils/helper";
import { fetchMainHolderBankAccounts } from "../../../services/api/accounts";
import style from "./InvestModal.scss";
import { BankAccountsSelector } from "../../BankAccountsSelector/BankAccountsSelector";
import { modes } from "../../Invest/FundsTable/FundsAssetTable/FundsAssetTable";

export const InvestModal = ({ modal, toggle, item }) => {
  const [value, setValue] = useState(0);
  const debValue = useDebounce(value, 300);
  const [activeAccount, setActiveAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [noFunds, setnoFunds] = useState(false);
  const [allBankAccountsAreInvalid, setAllBankAccountsAreInvalid] = useState(false);
  const [bankAccountsLoading, setBankAccountsLoading] = useState(true);
  const [loadingEarnings, setLoadingEarnings] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [anyError, setAnyError] = useState(false);
  const [fields, setFields] = useState({
    AssetCouponRate: 0,
    SettlementCurrency: "",
    SettlementCurrencyId: "",
    DebitTotal: 0,
    Commission: 0,
    BuyTotal: 0,
    Charges: 0,
    IVA: 0,
    Available: 0,
  });
  const personInfo = useSelector(getSavedPersonInfo);
  const currentAccount = useSelector(getCurrentAccount);
  const texts = useSelector(getTexts);

  useEffect(() => {
    if (!currentAccount.IsPhysical && texts.tooltips.juridicalBankAccounts) {
      getMainHolderBankAccounts();
    } else {
      if (personInfo.PersonBankAccount) {
        setBankAccounts(personInfo.PersonBankAccount);
      }
    }
  }, [personInfo, currentAccount]);

  useEffect(() => {
    if (!fields.SettlementCurrency) {
      const defaultCurrency = item.AssetInformation.SettlementCurrencies.find(
        (settlementCurrenciesItem) => settlementCurrenciesItem.SettlementCurrencyIsDefault
      );
      setFields((prevFields) => ({
        ...prevFields,
        SettlementCurrency: defaultCurrency.SettlementCurrencyISO,
        SettlementCurrencyId: defaultCurrency.SettlementCurrencyId,
      }));
    }
  }, []);

  useEffect(() => {
    if (!fields.SettlementCurrency) {
      const defaultCurrency = item.AssetInformation.SettlementCurrencies.find(
        (settlementCurrenciesItem) => settlementCurrenciesItem.SettlementCurrencyIsDefault
      );
      setFields((prevFields) => ({
        ...prevFields,
        SettlementCurrency: defaultCurrency.SettlementCurrencyISO,
      }));
    }
    if (currentAccount.id && fields.SettlementCurrency) {
      setLoadingEarnings(() => true);
      const OrderInitDate = moment().hour(8).format("YYYY-MM-DDTHH:mm:ss"); // Set time to 8 AM to avoid calculating earnings for previous day

      const order = {
        OrderInitDate,
        OrderSide: "Buy",
        AssetId: item.AssetInformation.AssetId,
        OrderSettlementCurrencySymbol: fields.SettlementCurrency,
        OrderPrice: item.OfferPrice,
        CurrentAccount: {
          AccountId: currentAccount.id,
          Quantity: +debValue,
        },
      };

      getEarnings({ order })
        .then(({ response }) => {
          if (response.result.Code !== 200) {
            showToast("Error obteniendo comisiones y cargos del servidor", true);
          } else {
            let newIVA = 0;
            newIVA =
              +response.order.CurrentAccount.Precheck.tax +
              +response.order.CurrentAccount.Precheck.taxupfront +
              +response.order.CurrentAccount.Precheck.taxfxupfront;

            let newCharges = 0;

            if (response.order.CurrentAccount.Precheck.Fees) {
              response.order.CurrentAccount.Precheck.Fees.map((feeitem) => {
                newCharges += +feeitem.feevalue;
              });
            }

            const newCommission = +response.order.CurrentAccount.Precheck.commission - newCharges;

            setFields((prevFields) => ({
              ...prevFields,
              BuyTotal:
                +response.order.CurrentAccount.Precheck.Principal +
                +response.order.CurrentAccount.Precheck.Accrual,
              Commission: newCommission,
              DebitTotal: +response.order.CurrentAccount.Precheck.Total,
              IVA: newIVA,
              Charges: newCharges,
            }));
          }
        })
        .catch((err) => {
          showToast("Error obteniendo comisiones y cargos del servidor", true);
        })
        .finally(() => {
          setLoadingEarnings(() => false);
        });
    }
  }, [item, debValue, currentAccount, fields.SettlementCurrency]);

  useEffect(() => {
    if (fields.Available === 0) {
      const available = item.OfferQuantitytoClient - item.OfferQuantityTakenByClient;
      setFields((prevFields) => ({
        ...prevFields,
        Available: available,
      }));
    }
    if (fields.AssetCouponRate === 0) {
      item.AssetInformation.Payments.map((paymentItem) => {
        const paymentItemAssetCouponDate = new Date(paymentItem.AssetCouponDate);
        if (new Date() < paymentItemAssetCouponDate) {
          setFields((prevFields) => ({
            ...prevFields,
            AssetCouponRate: +paymentItem.AssetCouponRate,
          }));
          return;
        }
      });
    }
  }, [item]);

  const getMainHolderBankAccounts = async () => {
    try {
      if (currentAccount && currentAccount.id) {
        const { response } = await fetchMainHolderBankAccounts(currentAccount.id);

        if (response && response.bankAccounts) {
          setBankAccounts(response.bankAccounts);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const executePrechecks = async () => {
    try {
      const { response } = await getPrechecks({
        order: {
          AssetId: item.AssetInformation.AssetId,
          AssetTicker: item.AssetInformation.AssetTicker,
          AssetClassFormatted: item.AssetInformation.AssetClassFormatted,
          OrderPrice: item.OfferPrice,
          OrderInitDate: moment().format("YYYY-MM-DDTHH:mm:ss"),
          cashdiffered: true,
          OrderQuantityIsCash: false,
          OrderSide: "Buy",
          OrderSettlementCurrencyId: fields.SettlementCurrencyId,
          CurrentAccount: {
            Precheck: {
              Total: +fields.DebitTotal,
            },
            HolderId: personInfo.PersonId,
            AccountId: currentAccount.id,
            clientBankAccount: activeAccount ? activeAccount.PersonBankAccountId : null,
            Quantity: +debValue,
          },
        },
      });

      if (response.result.Code !== 200) {
        showToast(response.result.Description, true);
        return false;
      }
      return true;
    } catch (err) {
      console.error(err);
      showToast("Error comprobando la inversión", true);
      return false;
    }
  };

  const validate = async () => {
    if (+value === 0) {
      showToast("La cantidad ingresada no puede ser cero.", true);
      return false;
    }
    if (+value > item.OfferQuantitytoClient) {
      showToast("La cantidad ingresada no puede ser mayor a la cantidad ofrecida.", true);
      return false;
    }
    if (!activeAccount) {
      showToast("Debe seleccionar una cuenta", true);
      return false;
    }

    const prechecks = await executePrechecks();
    if (!prechecks) return false;

    return true;
  };

  const onConfirm = async () => {
    setLoading(() => true);
    const validated = await validate();
    if (validated) {
      const payload = {
        offerid: item.OfferId,
        quantity: value,
        accountNumber: currentAccount.AccountNumber,
        bankAccountNumber: activeAccount.PersonBankAccountNumber,
      };
      takeOffer(payload)
        .then(({ response }) => {
          if (response.result.Code !== 200) {
            showToast(response.result.Description, true);
          } else {
            showToast("Se envió la solicitud de compra.");
            toggle(this);
          }
        })
        .catch((err) => {
          console.error(err);
          showToast("Error enviando la solicitud de compra.", true);
        })
        .finally(() => {
          setLoading(() => false);
        });
    } else {
      setLoading(() => false);
    }
  };

  const handleChange = (event) => {
    const result = event.target.value.replace(/\D/g, "");
    setValue(result);
    setActiveAccount(null);
  };

  const getPaymentString = (AssetCouponDividendPaymentBase, AssetCouponDividendPaymentFrecuency) => {
    let paymentString = AssetCouponDividendPaymentFrecuency;

    switch (AssetCouponDividendPaymentBase) {
      case "Days":
        if (AssetCouponDividendPaymentFrecuency === 1) {
          // paymentString += " dia";
          paymentString = "Diario";
        } else {
          paymentString = "Cada " + paymentString + " dias";
        }
        break;
      case "Weeks":
        if (AssetCouponDividendPaymentFrecuency === 1) {
          // paymentString += " semana";
          paymentString = "Semanal";
        } else {
          paymentString = "Cada " + paymentString + " semanas";
        }
        break;
      case "Months":
        if (AssetCouponDividendPaymentFrecuency === 1) {
          // paymentString += " mes";
          paymentString = "Mensual";
        } else if (AssetCouponDividendPaymentFrecuency === 2) {
          paymentString = "Bimensual";
        } else if (AssetCouponDividendPaymentFrecuency === 3) {
          paymentString = "Trimestral";
        } else if (AssetCouponDividendPaymentFrecuency === 6) {
          paymentString = "Semestral";
        } else {
          paymentString = "Cada " + paymentString + " meses";
        }
        break;
      case "Years":
        if (AssetCouponDividendPaymentFrecuency === 1) {
          // paymentString += " año";
          paymentString = "Anual";
        } else {
          paymentString = "Cada " + paymentString + " años";
        }
        break;
    }
    if (paymentString === "") return "-";
    return paymentString;
  };

  const getResidualTerm = () => {
    const today = moment(new Date());
    const expiration = moment(item.AssetInformation.AssetMaturityDate);
    if (!item.AssetInformation.AssetMaturityDate) return "-";
    return expiration.diff(today, "months");
  };

  const handleSetAnyError = () => {
    setAnyError(() => true);
  };

  const handleBankAccountsLoading = (isLoading) => {
    setBankAccountsLoading(isLoading);
  };

  const NoBankAccountsAvailable = () => {
    return (
      <div style={{ textAlign: "center", marginTop: "15px" }}>
        <span style={{ fontSize: "16px", fontWeight: "bold" }}>No tienes cuentas de banco disponibles</span>
      </div>
    );
  };

  const setAllBankAccountAreInvalid = (allAreInvalid, notEnoughFunds) => {
    setnoFunds(notEnoughFunds || noFunds);
    setAllBankAccountsAreInvalid(() => (allAreInvalid ? true : false));
  };

  return (
    <div className={style.subAssetClassDisplay}>
      <Row>
        <Col xs={12} className={style.titleWrapper}>
          <span className={style.iconWrapper} onClick={toggle}>
            <Icons.ArrowLeftIcon className={style.iconArrow} />
          </span>
          <span className={style.title}>Detalle de la inversión</span>
        </Col>
        <hr className={style.hr} />
      </Row>
      <Row>
        <div className={style.summary}>
          <Col className={style.firstCol} xs={3}>
            <span className={style.offerTitle}>
              {" "}
              {item.AssetName.substring(0, item.AssetName.indexOf("-"))}
            </span>
            <span className={style.valueContainer}>
              <span className={style.offerValue}> Valor: </span>
              <FormattedNumber
                value={(item.AssetInformation.AssetParValue * item.OfferPrice) / 100}
                minimumFractionDigits={item.AssetInformation.CurrencyISO === "PYG" ? 0 : 2}
                maximumFractionDigits={item.AssetInformation.CurrencyISO === "PYG" ? 0 : 2}
              />
              <span className={style.currency}> {item.AssetInformation.CurrencyISO}</span>
            </span>
            <span className={style.ISINContainer}>
              <span className={style.offerValue}> ISIN: </span>
              <span>
                {item.AssetInformation.AssetDefaultIdentifierValue
                  ? item.AssetInformation.AssetDefaultIdentifierValue
                  : "-"}
              </span>
            </span>
          </Col>
          <Col className={style.secondCol} xs={8}>
            <span className={style.propertyContainer}>
              <span className={style.propertyTitle}>Valor por unidad</span>
              <FormattedNumber
                value={item.OfferPrice}
                minimumFractionDigits={item.AssetInformation.CurrencyISO === "PYG" ? 4 : 4}
                maximumFractionDigits={item.AssetInformation.CurrencyISO === "PYG" ? 4 : 4}
              />
            </span>
            <span className={style.propertyContainer}>
              <span className={style.propertyTitle}> Rendimiento (TIR)</span>
              <span className={style.propertyValue}>
                <FormattedNumber
                  value={item.NominalRate}
                  minimumFractionDigits={2}
                  maximumFractionDigits={2}
                />
                %
              </span>
            </span>
            <span className={style.propertyContainer}>
              <span className={style.propertyTitle}> Tasa cupón</span>
              <span className={style.propertyValue}>
                <FormattedNumber
                  value={item.AssetInformation.AssetPaymentRate}
                  minimumFractionDigits={2}
                  maximumFractionDigits={2}
                />
                %
              </span>
            </span>
            <span className={style.propertyContainer}>
              <span className={style.propertyTitle}> Vencimiento</span>
              {item.AssetInformation.AssetMaturityDate === "0000-00-00"
                ? "-"
                : moment(item.AssetInformation.AssetMaturityDate).format("DD/MM/YYYY")}
            </span>
            <span className={style.propertyContainer}>
              <span className={style.propertyTitle}> Pago de interés</span>
              {getPaymentString(
                item.AssetInformation.AssetCouponDividendPaymentBase,
                item.AssetInformation.AssetCouponDividendPaymentFrecuency
              )}
            </span>
            <span className={style.propertyContainer}>
              <span className={style.propertyTitle}> Plazo residual</span>
              {item.AssetInformation.SubAssetClassInfo.SubAssetClassHasMaturity
                ? `${getResidualTerm()} meses`
                : "-"}
            </span>
          </Col>
        </div>
      </Row>
      <Row className={style.rowFirstStep}>
        <Col className={style.colFirstStep} xs={8}>
          <div className={style.quantityCol}>
            <div className={style.boxStep}>
              <div className={style.numberStep}> 1 </div>
              <div className={style.titleStep}>¿Cuánto querés comprar?</div>
            </div>
            <div className={style.quantityContainer}>
              <div className={style.quantityBox}>
                <div className={style.quantityTitle}>Cantidad</div>
                <div className={style.inputBox}>
                  <input
                    className={style.inputQuantity}
                    type="number"
                    min={0}
                    value={value}
                    onChange={handleChange}
                    max={fields.Available}
                  />
                  <Icons.PencilInput size="sm" className={style.iconPencil} />
                </div>
              </div>
              <div className={style.quantityAvailable}>
                <div>Cantidad disponible </div>
                <div className={style.amountQuantity}>{fields.Available}</div>
              </div>
            </div>
            <div className={style.offerDetails}>
              <div>
                <div className={style.detailTitle}>Valor</div>
                <div className={style.detailValue}>
                  <FormattedNumber
                    value={fields.BuyTotal}
                    minimumFractionDigits={item.AssetInformation.CurrencyISO === "PYG" ? 0 : 2}
                    maximumFractionDigits={item.AssetInformation.CurrencyISO === "PYG" ? 0 : 2}
                  />
                  <span className={style.currency}> {item.AssetInformation.CurrencyISO}</span>
                </div>
              </div>
              <div>
                <div className={style.detailTitle}>Arancel operativo</div>
                <div className={style.detailValue}>
                  <FormattedNumber
                    value={fields.Charges}
                    minimumFractionDigits={item.AssetInformation.CurrencyISO === "PYG" ? 0 : 2}
                    maximumFractionDigits={item.AssetInformation.CurrencyISO === "PYG" ? 0 : 2}
                  />
                  <span className={style.currency}> {item.AssetInformation.CurrencyISO}</span>
                </div>
              </div>
              <div>
                <div className={style.detailTitle}>IVA</div>
                <div className={style.detailValue}>
                  <FormattedNumber
                    value={fields.IVA}
                    minimumFractionDigits={item.AssetInformation.CurrencyISO === "PYG" ? 0 : 2}
                    maximumFractionDigits={item.AssetInformation.CurrencyISO === "PYG" ? 0 : 2}
                  />
                  <span className={style.currency}> {item.AssetInformation.CurrencyISO}</span>
                </div>
              </div>
              <div>
                <div className={style.detailTitle}>Comisión corretaje</div>
                <div className={style.detailValue}>
                  <FormattedNumber
                    value={fields.Commission}
                    minimumFractionDigits={item.AssetInformation.CurrencyISO === "PYG" ? 0 : 2}
                    maximumFractionDigits={item.AssetInformation.CurrencyISO === "PYG" ? 0 : 2}
                  />
                  <span className={style.currency}> {item.AssetInformation.CurrencyISO}</span>
                </div>
              </div>
            </div>
          </div>
        </Col>
        <Col xs={4} className={style.colAccount}>
          <div className={style.colSubAccount}>
            <div className={style.boxStep}>
              <div className={style.numberStep}> 2 </div>
              <div className={style.titleStep}>Elegí una cuenta para debitar</div>
            </div>
            {!bankAccountsLoading &&
              allBankAccountsAreInvalid &&
              (noFunds ? (
                <div className={style.invalidInput}>Acreditá saldo en tu cuenta para continuar.</div>
              ) : (
                <div className={style.invalidInput}>No tenés una cuenta para realizar la operación.</div>
              ))}
            <div className={style.boxAccount}>
              <Fragment>
                <span className={style.accountContainer}>
                  {!bankAccounts.filter((account) => account.PersonBankAccountStatus === "Active").length ? (
                    <NoBankAccountsAvailable />
                  ) : (
                    <BankAccountsSelector
                      bankAccounts={bankAccounts}
                      selectedBankAccount={activeAccount}
                      onSelectBankAccount={setActiveAccount}
                      disabled={loadingEarnings}
                      filters={{
                        currencyISO: fields.SettlementCurrency,
                        amount: +fields.DebitTotal,
                        mode: modes.BUY,
                      }}
                      setAnyError={handleSetAnyError}
                      setLoadingStates={handleBankAccountsLoading}
                      setAllBankAccountAreInvalid={setAllBankAccountAreInvalid}
                    />
                  )}
                </span>
              </Fragment>
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <div className={style.hr}></div>
      </Row>
      <Row className={style.confirmBuy}>
        <Col xs={8} className={style.totalAmountContainer}>
          <div className={style.totalContainer}>
            <div className={style.totalText}>Total a debitar: </div>
            <div className={style.totalAmount}>
              <FormattedNumber
                value={fields.DebitTotal}
                minimumFractionDigits={item.AssetInformation.CurrencyISO === "PYG" ? 0 : 2}
                maximumFractionDigits={item.AssetInformation.CurrencyISO === "PYG" ? 0 : 2}
              />
              <span className={style.currency}> {item.AssetInformation.CurrencyISO}</span>
            </div>
          </div>
        </Col>
        <Col className={style.buttonCol} xs={4}>
          <div className={style.buttonContainer}>
            <GenericButton
              label={"Confirmar compra"}
              rounded
              loading={loading || loadingEarnings}
              padding={"12px 64px"}
              disabled={!activeAccount || +value === 0 || +value > item.OfferQuantitytoClient}
              primary
              right
              handleClick={onConfirm}
              iconSvg={[{ width: "16px", item: confirmIcon }]}
            ></GenericButton>
          </div>
        </Col>
      </Row>
    </div>
  );
};
